import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

/**
 * SyncBook — Terms of Service
 *
 * Public, unauthenticated page. Referenced from:
 *  - Twilio A2P 10DLC campaign registration (termsAndConditionsUrl)
 *  - Footer link on the auth screens
 *  - Register page checkbox (implicit acceptance)
 *
 * Standard consumer-app terms. If the pricing model or service scope changes
 * materially (new subscription tier, new integration, etc.), bump the
 * `Last updated` date and flag signed-in users on next login.
 */
export default function Terms() {
  return (
    <div
      className="max-w-3xl mx-auto p-5 space-y-5 pb-16"
      data-testid="terms-page"
    >
      <div className="flex items-center justify-between">
        <Link
          to="/"
          className="nb-btn nb-btn-secondary !py-2 !px-3"
          data-testid="terms-back"
        >
          <ArrowLeft size={16} strokeWidth={3} /> Back
        </Link>
        <h1 className="font-display text-2xl font-black">Terms of Service</h1>
        <div className="w-[70px]" />
      </div>

      <p className="text-xs font-medium text-[#4a4a4a]">
        Last updated: February 22, 2026
      </p>

      {/* ================================================================
          SMS PROGRAM TERMS — dedicated section for A2P 10DLC compliance.
          Placed at the top so campaign reviewers can find every required
          disclosure without hunting. Structure follows CTIA / TCPA / A2P
          10DLC review guidelines.
          ================================================================ */}
      <section
        className="nb-card-lg p-5 space-y-3 text-sm"
        style={{ background: "#e5f0ff" }}
        data-testid="terms-sms-program"
      >
        <h2 className="font-display font-black text-xl">SMS Program Terms</h2>
        <p className="text-[13px] font-medium text-[#4a4a4a]">
          These SMS Program Terms are provided in addition to the general Terms
          of Service below and are specific to SyncBook&apos;s optional
          transactional SMS feature. By opting in to SMS you agree to the
          disclosures in this section.
        </p>

        <div className="space-y-2">
          <div>
            <div className="font-display font-black text-[13px]">
              1. Program name & description
            </div>
            <p>
              <b>SyncBook SMS Transaction Logging.</b> A transactional-only
              messaging program that lets you text short logging shorthands
              (e.g. <span className="font-mono">-4.50 coffee</span>) to
              SyncBook&apos;s number and receive a text-reply confirmation with
              your updated balance. The program sends no marketing content.
            </p>
          </div>

          <div>
            <div className="font-display font-black text-[13px]">
              2. Consent is optional and not a condition of purchase or service
            </div>
            <p>
              SMS opt-in is <b>entirely optional</b>. Consent to receive text
              messages is <b>not a condition of purchasing any product or
              service, nor a condition of creating or using a SyncBook account</b>.
              You can create a SyncBook account, use every feature (transactions,
              budgets, reconcile, forecasting, CSV import, receipt OCR), and
              subscribe to any paid tier without ever providing a phone number
              or opting in to SMS.
            </p>
          </div>

          <div>
            <div className="font-display font-black text-[13px]">
              3. How to opt in
            </div>
            <p>
              You may opt in from either <b>Profile → Text-in transactions</b>{" "}
              inside the app, or from the public opt-in page at{" "}
              <Link
                to="/sms-signup"
                data-testid="terms-sms-signup-link"
                className="underline font-bold"
              >
                sync-book.app/sms-signup
              </Link>
              . In both places you must (a) enter your mobile number and (b)
              actively check a TCPA consent checkbox that starts unchecked. No
              consent is captured passively or by silence.
            </p>
          </div>

          <div>
            <div className="font-display font-black text-[13px]">
              4. Message frequency
            </div>
            <p>
              Message frequency varies based on how often you use the service.
              A typical active user receives <b>approximately 1–5 messages per
              day</b>. There is no fixed schedule of automated messages — every
              message SyncBook sends is a reply to a message you first sent.
            </p>
          </div>

          <div>
            <div className="font-display font-black text-[13px]">
              5. Message & data rates may apply
            </div>
            <p>
              <b>Message and data rates may apply.</b> Your mobile carrier may
              charge you for messages sent or received under standard rates.
              SyncBook itself does not charge you for SMS. Contact your carrier
              if you have questions about your plan&apos;s messaging rates.
            </p>
          </div>

          <div>
            <div className="font-display font-black text-[13px]">
              6. How to opt out (STOP)
            </div>
            <p>
              You may cancel SMS at any time by texting <b>STOP</b>,{" "}
              <b>END</b>, <b>QUIT</b>, <b>CANCEL</b>, or <b>UNSUBSCRIBE</b> to
              our SMS number, or by unlinking your phone from Profile → Text-in
              transactions inside the app. After you send STOP you will receive
              one final confirmation message and no further SMS will be sent
              to that number.
            </p>
          </div>

          <div>
            <div className="font-display font-black text-[13px]">7. Help (HELP)</div>
            <p>
              For assistance, text <b>HELP</b> or <b>INFO</b> to our SMS number
              at any time and you will receive a summary of supported commands
              plus support contact information. You can also email{" "}
              <a
                className="underline font-bold"
                href="mailto:support@sync-book.app"
              >
                support@sync-book.app
              </a>
              .
            </p>
          </div>

          <div>
            <div className="font-display font-black text-[13px]">
              8. Supported carriers
            </div>
            <p>
              Supported by major US wireless carriers including AT&amp;T,
              Verizon Wireless, T-Mobile, Sprint, Boost, Cricket, MetroPCS,
              U.S. Cellular, and Virgin Mobile USA. T-Mobile is not liable for
              delayed or undelivered messages. Carriers are not liable for
              delayed or undelivered messages.
            </p>
          </div>

          <div>
            <div className="font-display font-black text-[13px]">
              9. No third-party sharing for marketing
            </div>
            <p>
              <b>
                Your phone number and SMS content will never be sold, rented,
                or shared with third parties for marketing or promotional
                purposes.
              </b>{" "}
              We share phone-related data only with Twilio (our SMS carrier) to
              deliver replies to you. This information is not shared with
              affiliates or third-party marketers under any circumstances. See
              our{" "}
              <Link className="underline font-bold" to="/privacy">
                Privacy Policy
              </Link>{" "}
              for full data-handling details.
            </p>
          </div>

          <div>
            <div className="font-display font-black text-[13px]">
              10. Content of messages
            </div>
            <p>
              SMS content is limited to <b>transaction confirmations, balance
              summaries, and STOP/HELP replies</b>. SyncBook does not send
              marketing, promotional, informational, or third-party content
              over SMS.
            </p>
          </div>

          <div>
            <div className="font-display font-black text-[13px]">
              11. Privacy
            </div>
            <p>
              Read our{" "}
              <Link className="underline font-bold" to="/privacy">
                Privacy Policy
              </Link>{" "}
              for how we store and process phone numbers and SMS content.
            </p>
          </div>

          <div>
            <div className="font-display font-black text-[13px]">
              12. Contact
            </div>
            <p>
              Questions or complaints about the SMS program:{" "}
              <a
                className="underline font-bold"
                href="mailto:support@sync-book.app"
              >
                support@sync-book.app
              </a>
              .
            </p>
          </div>

          <div data-testid="terms-sms-samples">
            <div className="font-display font-black text-[13px]">
              13. Sample messages
            </div>
            <p>
              The messages consumers receive under this program are limited to
              the categories below. Every outbound message is a reply to a
              message the consumer first sent.
            </p>

            <div className="mt-2 space-y-2">
              <div className="p-2 border-2 border-[#1a1a1a] rounded-lg bg-white">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#4a4a4a]">
                  Confirmation reply — after user texts a transaction
                </div>
                <div className="text-[11px] font-mono mt-1">
                  User: <span className="font-bold">-4.50 coffee</span>
                </div>
                <div className="text-[11px] font-mono">
                  SyncBook: <span className="font-bold">OK 1: -4.50 coffee | Bal 233.50</span>
                </div>
              </div>

              <div className="p-2 border-2 border-[#1a1a1a] rounded-lg bg-white">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#4a4a4a]">
                  Balance digest — after user texts &ldquo;balance&rdquo;
                </div>
                <div className="text-[11px] font-mono mt-1">
                  User: <span className="font-bold">balance</span>
                </div>
                <div className="text-[11px] font-mono whitespace-pre-wrap">
                  SyncBook:{" "}
                  <span className="font-bold">
                    Main Checking: $233.50{"\n"}Savings: $500.00
                  </span>
                </div>
              </div>

              <div className="p-2 border-2 border-[#1a1a1a] rounded-lg bg-white">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#4a4a4a]">
                  Help reply — after user texts &ldquo;HELP&rdquo; or &ldquo;menu&rdquo;
                </div>
                <div className="text-[11px] font-mono mt-1">
                  User: <span className="font-bold">HELP</span>
                </div>
                <div className="text-[11px] font-mono">
                  SyncBook:{" "}
                  <span className="font-bold">
                    SyncBook: &apos;-4.50 coffee&apos;, &apos;+150 pay&apos;.
                    Chain w/ &apos;;&apos;: &apos;-4.50 coffee;-12 lunch&apos;.
                    balance | recent | menu | acct:name | force
                  </span>
                </div>
              </div>

              <div className="p-2 border-2 border-[#1a1a1a] rounded-lg bg-white">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#4a4a4a]">
                  Opt-out confirmation — after user texts &ldquo;STOP&rdquo;
                </div>
                <div className="text-[11px] font-mono mt-1">
                  User: <span className="font-bold">STOP</span>
                </div>
                <div className="text-[11px] font-mono">
                  SyncBook (via Twilio):{" "}
                  <span className="font-bold">
                    You have successfully been unsubscribed. You will not
                    receive any more messages from this number. Reply START to
                    resubscribe.
                  </span>
                </div>
              </div>

              <div className="p-2 border-2 border-[#1a1a1a] rounded-lg bg-white">
                <div className="text-[10px] font-bold uppercase tracking-wider text-[#4a4a4a]">
                  Unregistered-phone reply — the very first message from any
                  new number (no consent captured)
                </div>
                <div className="text-[11px] font-mono mt-1">
                  User: <span className="font-bold">hi</span>
                </div>
                <div className="text-[11px] font-mono">
                  SyncBook:{" "}
                  <span className="font-bold">
                    This number isn&apos;t linked to a SyncBook account. Log in
                    and add your phone under Profile → SMS.
                  </span>
                </div>
              </div>
            </div>

            <p className="mt-2 text-[12px] font-medium text-[#4a4a4a] italic">
              SyncBook does not send unsolicited messages, marketing content,
              informational alerts, promotional offers, or third-party content.
              Every message above is triggered by an inbound message from the
              consumer.
            </p>
          </div>
        </div>
      </section>

      <section className="nb-card p-5 space-y-3 text-sm">
        <h2 className="font-display font-black text-lg">1. Acceptance</h2>
        <p>
          By creating an account on <b>sync-book.app</b> (&quot;SyncBook&quot;,
          &quot;we&quot;, &quot;us&quot;), you agree to these Terms of Service
          and to our{" "}
          <Link
            className="underline font-bold"
            to="/privacy"
            data-testid="terms-to-privacy"
          >
            Privacy Policy
          </Link>
          . If you don&apos;t agree, don&apos;t use the service.
        </p>
      </section>

      <section className="nb-card p-5 space-y-3 text-sm">
        <h2 className="font-display font-black text-lg">2. What SyncBook does</h2>
        <p>
          SyncBook is a personal-finance app designed to help teenagers and
          their families track spending, reconcile bank statements, and forecast
          balances. Features include manual transaction entry, receipt-image
          OCR, CSV bank-statement import, budgets, savings goals, weekly
          summaries, and optional SMS logging.
        </p>
        <p>
          <b>SyncBook is not a bank.</b> We don&apos;t hold your money, move
          money, or connect to your bank account. All balance figures shown in
          the app come from what you (or your imported CSVs) tell us. Always
          rely on your bank&apos;s official statements for legal purposes.
        </p>
      </section>

      <section className="nb-card p-5 space-y-3 text-sm">
        <h2 className="font-display font-black text-lg">3. Your account</h2>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>You must be at least 13 years old to create an account.</li>
          <li>
            You&apos;re responsible for keeping your password secret and for all
            activity under your account.
          </li>
          <li>
            Don&apos;t share your account. If you&apos;re a parent, use the
            Family plan&apos;s Parent View feature to share read-only.
          </li>
          <li>
            Provide accurate information — misuse (e.g. false identity, spoofed
            phone numbers) may result in account termination.
          </li>
        </ul>
      </section>

      <section className="nb-card p-5 space-y-3 text-sm">
        <h2 className="font-display font-black text-lg">4. Acceptable use</h2>
        <p>You agree not to:</p>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>Use SyncBook for anything illegal or fraudulent.</li>
          <li>
            Attempt to reverse engineer, decompile, or bypass security controls.
          </li>
          <li>
            Send spam, harassment, or automated traffic through SyncBook&apos;s
            SMS gateway.
          </li>
          <li>
            Impersonate anyone or claim affiliation with SyncBook you don&apos;t
            have.
          </li>
          <li>
            Upload receipt images containing content you don&apos;t have the
            right to share.
          </li>
        </ul>
      </section>

      <section className="nb-card p-5 space-y-3 text-sm">
        <h2 className="font-display font-black text-lg">5. Paid plans</h2>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>
            SyncBook offers <b>Free</b>, <b>Pro</b>, and <b>Family</b> tiers.
            Feature lists and pricing are shown on the Upgrade page and may
            change with 30-day notice.
          </li>
          <li>
            Paid plans are billed via Stripe, monthly or yearly. A 7-day free
            trial applies to new Pro subscribers.
          </li>
          <li>
            You can cancel any time from Profile → Manage Subscription. Access
            continues through the end of the current billing period; no
            pro-rated refunds.
          </li>
          <li>
            If a payment fails, we may downgrade your account to Free until
            payment resumes.
          </li>
        </ul>
      </section>

      <section className="nb-card p-5 space-y-3 text-sm">
        <h2 className="font-display font-black text-lg">6. SMS terms</h2>
        <p>
          SyncBook offers an <b>optional</b> transactional SMS feature. SMS
          opt-in is not required to use SyncBook or any of its features. The
          complete disclosures for this program (frequency, rates, opt-out
          method, no-third-party-sharing statement, supported carriers, etc.)
          are provided in the{" "}
          <b>&ldquo;SMS Program Terms&rdquo;</b> section at the top of this
          page and in our{" "}
          <Link className="underline font-bold" to="/privacy">
            Privacy Policy
          </Link>
          .
        </p>
      </section>

      <section className="nb-card p-5 space-y-3 text-sm">
        <h2 className="font-display font-black text-lg">7. Content ownership</h2>
        <p>
          You own the data you enter (transactions, receipts, notes). By using
          SyncBook, you grant us a limited license to store, process, and
          display that data solely to provide the service. We claim no rights
          to your financial data.
        </p>
      </section>

      <section className="nb-card p-5 space-y-3 text-sm">
        <h2 className="font-display font-black text-lg">8. Termination</h2>
        <ul className="list-disc pl-5 space-y-1.5">
          <li>You can delete your account any time from Profile → Delete Account.</li>
          <li>
            We may suspend or terminate your account for material breach of
            these Terms (fraud, abuse, non-payment, illegal use).
          </li>
          <li>
            On termination, your data is removed per our{" "}
            <Link className="underline font-bold" to="/privacy">
              Privacy Policy
            </Link>
            .
          </li>
        </ul>
      </section>

      <section className="nb-card p-5 space-y-3 text-sm">
        <h2 className="font-display font-black text-lg">9. Disclaimers</h2>
        <p>
          SyncBook is provided <b>&quot;as is&quot;</b>. We don&apos;t guarantee
          uninterrupted service, error-free forecasts, or that OCR / CSV /
          category-suggest results will always be correct — always double-check
          against your bank&apos;s official records.
        </p>
        <p>
          Nothing in SyncBook is financial, tax, or investment advice. If you
          need those, talk to a qualified professional.
        </p>
      </section>

      <section className="nb-card p-5 space-y-3 text-sm">
        <h2 className="font-display font-black text-lg">
          10. Limitation of liability
        </h2>
        <p>
          To the maximum extent permitted by law, SyncBook, its owner, and its
          service providers are not liable for indirect, incidental, or
          consequential damages arising from your use of the service. Our total
          liability in any 12-month period is limited to the amount you paid us
          in that period (up to $100 for Free-tier users).
        </p>
      </section>

      <section className="nb-card p-5 space-y-3 text-sm">
        <h2 className="font-display font-black text-lg">11. Changes to terms</h2>
        <p>
          We may update these Terms occasionally. Material changes will be shown
          in-app before you continue using the service. Non-material updates are
          reflected by bumping the &quot;Last updated&quot; date.
        </p>
      </section>

      <section className="nb-card p-5 space-y-3 text-sm">
        <h2 className="font-display font-black text-lg">12. Governing law</h2>
        <p>
          These Terms are governed by the laws of the state in which SyncBook is
          registered, without regard to conflict-of-laws principles. Disputes
          will be resolved in the state and federal courts of that jurisdiction.
        </p>
      </section>

      <section className="nb-card p-5 space-y-3 text-sm">
        <h2 className="font-display font-black text-lg">13. Contact</h2>
        <p>
          Questions:{" "}
          <a
            className="underline font-bold"
            href="mailto:support@sync-book.app"
          >
            support@sync-book.app
          </a>
          .
        </p>
      </section>

      <div className="text-center text-xs font-medium text-[#4a4a4a] pt-2">
        <Link className="underline" to="/privacy">
          Privacy Policy
        </Link>
      </div>
    </div>
  );
}
